import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { documentAPI } from '../services/api';
import GoogleAuthButton from '../components/GoogleAuthButton';
import {
  CheckIcon,
  ScaleIcon,
  LockIcon,
  AlertCircleIcon,
  BuildingIcon
} from '../components/Icons';
import './Auth.css';

const LoginNew: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const { getLocalizedPath } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);

      // Check if user has any documents
      const documentsResponse = await documentAPI.list();
      const hasDocuments = documentsResponse.data.total > 0;

      // Redirect based on whether user has documents
      if (hasDocuments) {
        navigate(getLocalizedPath('/catalog'));
      } else {
      navigate(getLocalizedPath('/upload'));
      }
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || t('auth:login.errorIncorrectCredentials');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (accessToken: string) => {
    setLoading(true);
    setError('');

    try {
      await googleLogin(accessToken);

      // Check if user has any documents
      const documentsResponse = await documentAPI.list();
      const hasDocuments = documentsResponse.data.total > 0;

      // Redirect based on whether user has documents
      if (hasDocuments) {
        navigate(getLocalizedPath('/catalog'));
      } else {
      navigate(getLocalizedPath('/upload'));
      }
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || t('auth:login.errorGoogle');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(t('auth:login.errorGoogle'));
  };

  return (
    <div className="auth-page auth-page--refined auth-page--login">
      {/* Header */}
      <header className="auth-header">
        <div className="auth-header-content">
          <div className="auth-logo" onClick={() => navigate(getLocalizedPath('/'))}>
            <img src="/logo_transp.png" alt="ContratoSeguro" />
          </div>
          <div className="auth-header-actions">
            <button onClick={() => navigate(getLocalizedPath('/'))} className="btn-text">
              {t('auth:login.backToHome')}
            </button>
            <button onClick={() => navigate(getLocalizedPath('/signup'))} className="btn-primary-auth">
              {t('auth:login.createAccount')}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="auth-main">
        <div className="auth-container">
          <div className="auth-grid">
            {/* Left Column - Form */}
            <div className="auth-form-column">
              <div className="auth-card">
                <div className="auth-card-header">
                  <h1>{t('auth:login.welcomeBack')}</h1>
                  <p>{t('auth:login.subtitle')}</p>
                </div>

                {error && (
                  <div className="auth-error">
                    <AlertCircleIcon size={20} />
                    <span>{error}</span>
                  </div>
                )}

                <GoogleAuthButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  text={t('auth:login.googleButton')}
                />

                <div className="auth-divider">
                  <span>{t('common:or')}</span>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="form-field">
                    <label htmlFor="email">{t('auth:login.emailLabel')}</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth:login.emailPlaceholder')}
                      required
                      disabled={loading}
                      autoComplete="email"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="password">{t('auth:login.passwordLabel')}</label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      disabled={loading}
                      autoComplete="current-password"
                    />
                  </div>

                  <button type="submit" className="btn-primary-full" disabled={loading}>
                    {loading ? t('auth:login.loggingIn') : t('auth:login.submitButton')}
                  </button>

                  <div className="auth-secondary-action">
                    <a href={getLocalizedPath('/recuperar-senha')} className="link-secondary">{t('auth:login.forgotPassword')}</a>
                  </div>
                </form>

                <div className="auth-trust-footer">
                  <LockIcon size={16} />
                  <span>{t('auth:login.dataProtected')}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Presentation */}
            <div className="auth-presentation-column">
              <div className="auth-presentation">
                <h2>{t('auth:login.presentationTitle')}</h2>

                <div className="presentation-bullets">
                  <div className="presentation-bullet">
                    <CheckIcon size={18} />
                    <span>{t('auth:login.bullet1')}</span>
                  </div>
                  <div className="presentation-bullet">
                    <AlertCircleIcon size={18} />
                    <span>{t('auth:login.bullet2')}</span>
                  </div>
                  <div className="presentation-bullet">
                    <ScaleIcon size={18} />
                    <span>{t('auth:login.bullet3')}</span>
                  </div>
                </div>

                <div className="presentation-trust">
                  <div className="trust-badge">
                    <LockIcon size={16} />
                    <span>{t('common:trustBadges.lgpd')}</span>
                  </div>
                  <div className="trust-badge">
                    <BuildingIcon size={16} />
                    <span>{t('common:trustBadges.encrypted')}</span>
                  </div>
                  <div className="trust-badge">
                    <span>🇧🇷</span>
                    <span>{t('common:trustBadges.madeForBrazil')}</span>
                  </div>
                </div>

                <div className="presentation-privacy">
                  <LockIcon size={16} />
                  <span>{t('auth:login.privacyNote')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="auth-footer-section">
        <div className="auth-footer-content">
          <div className="footer-left">
            <img src="/logo_transp.png" alt="ContratoSeguro" className="footer-logo-small" />
            <div className="footer-copy">
              <p className="footer-brand">ContratoSeguro</p>
              <p className="footer-tagline">{t('common:footer.tagline')}</p>
            </div>
          </div>
          <div className="footer-center">
            <a href={getLocalizedPath('/termos-de-uso')}>{t('common:footer.termsOfUse')}</a>
            <a href={getLocalizedPath('/politica-de-privacidade')}>{t('common:footer.privacyPolicy')}</a>
            <a href={getLocalizedPath('/politica-de-cookies')}>{t('common:footer.cookiesPolicy')}</a>
            <a href={getLocalizedPath('/aviso-legal')}>{t('common:footer.legalNotice')}</a>
          </div>
          <div className="footer-right">
            <p className="footer-heading">{t('common:footer.contact')}</p>
            <p className="footer-contact">suporte@contratoseguro.com.br</p>
            <p className="footer-heading">{t('common:footer.lgpd')}</p>
            <p className="footer-contact">privacidade@contratoseguro.com.br</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginNew;
